(() => {
  'use strict';

  const SELECTORS = {
    colourVariants: 'product-colour-variants',
    coloursContainer: '.product__colours',
    colourItems: '.product__colours-item.js-variant-colour',
    dropdown: '.variant-colour-dropdown',
    trigger: '.variant-colour-dropdown__trigger',
    list: '.variant-colour-dropdown__list',
    item: '.variant-colour-dropdown__item',
    staticDropdown: 'variant-colour-dropdown',
  };

  const CLASSES = {
    block: 'variant-colour-dropdown',
    trigger: 'variant-colour-dropdown__trigger',
    swatch: 'variant-colour-dropdown__swatch',
    label: 'variant-colour-dropdown__label',
    chevron: 'variant-colour-dropdown__chevron',
    list: 'variant-colour-dropdown__list',
    item: 'variant-colour-dropdown__item',
    itemActive: 'variant-colour-dropdown__item--active',
    modOpen: 'variant-colour-dropdown--open',
  };

  let currentVariant = 'control';

  const getSwatchUrl = (el) => el.querySelector('img')?.src || null;
  const getSwatchColour = (el) => {
    const url = getSwatchUrl(el);
    if (url) return { type: 'image', value: url };
    return null;
  };
  const getProductUrl = (el) => el.getAttribute('data-product');
  const oosArm = () => document.documentElement.getAttribute('data-oos-swatches');
  const oosArmOn = () => oosArm() === 'variant' || oosArm() === 'hide';
  const oosArmHides = () => oosArm() === 'hide';
  const getLabel = (el) => {
    const colourName = el.getAttribute('data-colour-name');
    if (colourName) return colourName;
    const img = el.querySelector('img');
    return img ? (img.alt || '') : el.textContent.trim();
  };

  const applySwatchStyle = (swatchEl, swatch) => {
    if (!swatch) return;
    if (swatch.type === 'colour') {
      swatchEl.style.backgroundColor = swatch.value;
      swatchEl.style.backgroundImage = '';
    } else {
      swatchEl.style.backgroundImage = `url(${swatch.value})`;
      swatchEl.style.backgroundColor = '';
    }
  };

  const buildDropdown = (colourVariantsEl) => {
    const items = [...colourVariantsEl.querySelectorAll(SELECTORS.colourItems)];
    if (!items.length) return null;

    const activeItem = items.find((el) => el.classList.contains('active')) || items[0];

    const dropdown = document.createElement('div');
    dropdown.className = CLASSES.block;
    dropdown.setAttribute('role', 'combobox');
    dropdown.setAttribute('aria-expanded', 'false');
    dropdown.setAttribute('aria-haspopup', 'listbox');

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = CLASSES.trigger;
    trigger.setAttribute('aria-label', 'Select colour');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-haspopup', 'listbox');

    const triggerSwatch = document.createElement('span');
    triggerSwatch.className = CLASSES.swatch;
    applySwatchStyle(triggerSwatch, getSwatchColour(activeItem));

    const triggerLabel = document.createElement('span');
    triggerLabel.className = CLASSES.label;
    triggerLabel.textContent = getLabel(activeItem);

    const triggerChevron = document.createElement('span');
    triggerChevron.className = CLASSES.chevron;
    triggerChevron.setAttribute('aria-hidden', 'true');
    triggerChevron.innerHTML = `<svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>`;

    trigger.append(triggerSwatch, triggerLabel, triggerChevron);

    const listId = `variant-colour-dropdown-list-${Math.random().toString(36).slice(2, 9)}`;
    trigger.setAttribute('aria-controls', listId);

    const list = document.createElement('ul');
    list.id = listId;
    list.className = CLASSES.list;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-label', 'Colour options');

    items.forEach((item) => {
      // CRO-105 — drop sold-out options, but never the one we landed on.
      if (oosArmHides() && item.getAttribute('data-available') === 'false' && !item.hasAttribute('data-landed')) {
        return;
      }

      const isActive = item.classList.contains('active');

      const li = document.createElement('li');
      li.className = isActive ? `${CLASSES.item} ${CLASSES.itemActive}` : CLASSES.item;
      li.setAttribute('role', 'option');
      li.setAttribute('tabindex', '0');
      li.setAttribute('data-product', getProductUrl(item));
      li.setAttribute('aria-selected', isActive ? 'true' : 'false');

      // CRO-105 — inert until an arm turns it into a disabled state.
      if (item.getAttribute('data-available') === 'false') {
        li.setAttribute('data-available', 'false');
        if (oosArmOn()) li.setAttribute('aria-disabled', 'true');
      }

      const swatch = document.createElement('span');
      swatch.className = CLASSES.swatch;
      applySwatchStyle(swatch, getSwatchColour(item));

      const label = document.createElement('span');
      label.className = CLASSES.label;
      label.textContent = getLabel(item);

      li.append(swatch, label);
      list.appendChild(li);
    });

    dropdown.append(trigger, list);
    return dropdown;
  };

  const openDropdown = (dropdown) => {
    // A PDP can have several (colour + size); the trigger's stopPropagation means
    // the click-outside listener never closes the others.
    document.querySelectorAll(`.${CLASSES.modOpen}`).forEach((other) => {
      if (other !== dropdown) closeDropdown(other);
    });
    dropdown.classList.add(CLASSES.modOpen);
    dropdown.setAttribute('aria-expanded', 'true');
    dropdown.querySelector(SELECTORS.trigger)?.setAttribute('aria-expanded', 'true');
  };

  const closeDropdown = (dropdown) => {
    dropdown.classList.remove(CLASSES.modOpen);
    dropdown.setAttribute('aria-expanded', 'false');
    dropdown.querySelector(SELECTORS.trigger)?.setAttribute('aria-expanded', 'false');
  };

  const updateTrigger = (dropdown, selectedItem) => {
    const swatch = dropdown.querySelector(`.${CLASSES.trigger} .${CLASSES.swatch}`);
    const label = dropdown.querySelector(`.${CLASSES.trigger} .${CLASSES.label}`);
    const selectedSwatch = selectedItem.querySelector(`.${CLASSES.swatch}`);

    if (swatch && selectedSwatch) swatch.style.backgroundImage = selectedSwatch.style.backgroundImage;
    if (label) label.textContent = selectedItem.querySelector(`.${CLASSES.label}`)?.textContent || '';
  };

  const setActiveItem = (dropdown, activeItem) => {
    dropdown.querySelectorAll(SELECTORS.item).forEach((item) => {
      const isMatch = item === activeItem;
      item.classList.toggle(CLASSES.itemActive, isMatch);
      item.setAttribute('aria-selected', isMatch ? 'true' : 'false');
    });
    updateTrigger(dropdown, activeItem);
  };

  // Picking an item clicks the swatch it mirrors, so the colour swap stays in product-colour-variants.js.
  const selectSwatch = (colourVariantsEl) => (item) => {
    const productUrl = item.getAttribute('data-product');
    const originalItem = colourVariantsEl.querySelector(
      `${SELECTORS.colourItems}[data-product="${productUrl}"]`
    );
    if (originalItem && productUrl !== window.location.pathname) {
      originalItem.click();
      return true;
    }
    return false;
  };

  const revealGallery = (dropdown) => {
    if (dropdown.closest('quick-add-modal')) return;
    const gallery = document.querySelector('[id^="productMainSlider-"]');
    if (gallery) window.revealProductGallery?.(gallery);
  };

  const bindEvents = (dropdown, onSelect, signal) => {
    const trigger = dropdown.querySelector(SELECTORS.trigger);
    const list = dropdown.querySelector(SELECTORS.list);

    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      dropdown.classList.contains(CLASSES.modOpen) ? closeDropdown(dropdown) : openDropdown(dropdown);
    }, { signal });

    list.addEventListener('click', (e) => {
      const item = e.target.closest(SELECTORS.item);
      if (!item) return;
      if (oosArmOn() && item.getAttribute('data-available') === 'false') return;

      setActiveItem(dropdown, item);
      closeDropdown(dropdown);
      onSelect(item);
    }, { signal });

    document.addEventListener('click', (e) => {
      if (!dropdown.contains(e.target)) closeDropdown(dropdown);
    }, { signal });

    dropdown.addEventListener('keydown', (e) => {
      // Skips items the CRO-105 hide arm has display: none'd, which can't take focus.
      const listItems = [...list.querySelectorAll(SELECTORS.item)].filter((item) => item.getClientRects().length);
      const activeIndex = listItems.indexOf(document.activeElement);

      if (e.key === 'Escape') {
        closeDropdown(dropdown);
        trigger.focus();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        (listItems[activeIndex + 1] || listItems[0])?.focus();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        (listItems[activeIndex - 1] || listItems[listItems.length - 1])?.focus();
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        list.querySelector(':focus')?.click();
      }
    }, { signal });
  };

  const syncActiveState = (
    dropdown = document.querySelector(`${SELECTORS.colourVariants} ${SELECTORS.dropdown}`),
    colourVariantsEl = document.querySelector(SELECTORS.colourVariants)
  ) => {
    if (!dropdown || !colourVariantsEl) return;

    const activeOriginal = colourVariantsEl.querySelector(`${SELECTORS.colourItems}.active`);
    if (!activeOriginal) return;

    const activeUrl = getProductUrl(activeOriginal);
    const activeItem = [...dropdown.querySelectorAll(SELECTORS.item)].find(
      (item) => item.getAttribute('data-product') === activeUrl
    );
    if (activeItem) setActiveItem(dropdown, activeItem);
  };

  const injectDropdown = () => {
    const colourVariantsEl = document.querySelector(SELECTORS.colourVariants);
    if (!colourVariantsEl) return;
    if (colourVariantsEl.querySelector(SELECTORS.dropdown)) return;

    const coloursContainer = colourVariantsEl.querySelector(SELECTORS.coloursContainer);
    if (!coloursContainer) return;

    const dropdown = buildDropdown(colourVariantsEl);
    if (!dropdown) return;

    bindEvents(dropdown, selectSwatch(colourVariantsEl));
    coloursContainer.style.setProperty('display', 'none', 'important');
    coloursContainer.insertAdjacentElement('beforebegin', dropdown);
  };

  // Only ever removes the JS-built test dropdown, never the Liquid-rendered element.
  const removeDropdown = () => {
    const dropdown = document.querySelector(`${SELECTORS.dropdown}:not(${SELECTORS.staticDropdown})`);
    if (!dropdown) return;

    dropdown.parentElement.querySelector(SELECTORS.coloursContainer)?.style.removeProperty('display');
    dropdown.remove();
  };

  // Liquid renders this for mixandmatch products, over either the alternative_products
  // swatches or a real option's radios (data-source="radios"). A custom element so it
  // also binds inside quick-add, where injected scripts don't re-run on a PDP.
  class VariantColourDropdown extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      if (this.dataset.source === 'radios') {
        const fieldset = this.closest('fieldset');
        if (!fieldset) return;

        const radioFor = (value) =>
          [...fieldset.querySelectorAll('input[type="radio"]')].find((radio) => radio.value === value);

        bindEvents(this, (item) => {
          const radio = radioFor(item.dataset.value);
          if (!radio || radio.checked) return;
          radio.click();
          revealGallery(this);
        }, signal);

        fieldset.addEventListener('change', () => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          const activeItem = [...this.querySelectorAll(SELECTORS.item)].find(
            (item) => item.dataset.value === checked?.value
          );
          if (activeItem) setActiveItem(this, activeItem);
        }, { signal });
        return;
      }

      const colourVariantsEl = this.closest(SELECTORS.colourVariants);
      if (!colourVariantsEl) return;

      const select = selectSwatch(colourVariantsEl);
      bindEvents(this, (item) => {
        if (select(item)) revealGallery(this);
      }, signal);
      document.addEventListener(
        'colorVariantChanged',
        () => setTimeout(() => syncActiveState(this, colourVariantsEl), 300),
        { signal }
      );
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }
  }

  if (!customElements.get('variant-colour-dropdown')) {
    customElements.define('variant-colour-dropdown', VariantColourDropdown);
  }

  // Re-inject after fake colour navigation replaces the DOM
  document.addEventListener('colorVariantChanged', () => {
    if (currentVariant === 'v2') {
      setTimeout(() => {
        removeDropdown();
        injectDropdown();
        syncActiveState();
      }, 300);
    }
  });

  window.variantColourDropdown = {
    control() {
      currentVariant = 'control';
      removeDropdown();
      document.documentElement.classList.remove('variant-colour-dropdown--active');
      document.documentElement.removeAttribute('data-variant-colour-dropdown');
    },
    v2() {
      currentVariant = 'v2';
      injectDropdown();
      document.documentElement.classList.add('variant-colour-dropdown--active');
      document.documentElement.setAttribute('data-variant-colour-dropdown', 'v2');
    },
    reset() {
      this.control();
    },
  };

  document.addEventListener('page:load', () => window.variantColourDropdown.control?.());
})();
